<script setup>
import { formatDisplayDate } from "~/utils/dates";

const { t } = useI18n();

const props = defineProps({ data: { type: Object, required: true } });

// Resolved media_list items carry the ISO DateField value; static
// media_article blocks may hold free text — both unify to yyyy/mm/dd.
const displayDate = computed(() => formatDisplayDate(props.data.date));

// The prominent image is the dedicated cover; fall back to the outlet logo so
// older entries (which stored the article photo under `logo`) still feature it.
const coverImage = computed(() => props.data.cover || props.data.logo);
// Show the logo as a small badge on the outlet line only when it's distinct
// from the cover (otherwise the cover already is the logo).
const badgeLogo = computed(() =>
  props.data.cover && props.data.logo ? props.data.logo : null,
);
</script>

<template>
  <!-- Mirrors EventCardBlock's default (compact) variant so news and event
       cards read as siblings on the home page. News-specific: external link,
       outlet meta line (in place of the event's location), no-cover placeholder. -->
  <a
    :href="data.url"
    target="_blank"
    rel="noopener"
    class="card group flex h-full flex-col overflow-hidden p-0 no-underline"
  >
    <!-- Cover photo: the prominent visual for the article -->
    <div v-if="coverImage" class="relative aspect-[3/2] overflow-hidden">
      <img
        :src="coverImage.url"
        :alt="coverImage.alt_text || data.headline"
        loading="lazy"
        class="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <div aria-hidden="true" class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
    </div>

    <!-- No cover: branded placeholder banner so the grid stays even -->
    <div
      v-else
      class="relative grid aspect-[3/2] place-items-center bg-rainbow-line text-paper"
      aria-hidden="true"
    >
      <span class="font-display text-5xl font-bold">{{ data.outlet.charAt(0) }}</span>
    </div>

    <div class="flex flex-1 flex-col p-6">
      <div class="space-y-2.5">
        <div v-if="displayDate" class="text-sm font-semibold tracking-wide text-brand-orange">{{ displayDate }}</div>
        <h3 class="font-display text-xl font-bold leading-snug transition-colors group-hover:text-brand-green">
          {{ data.headline }}
        </h3>
        <p v-if="data.outlet" class="text-sm text-muted flex items-center gap-1.5">
          <img
            v-if="badgeLogo"
            :src="badgeLogo.url"
            :alt="badgeLogo.alt_text || data.outlet"
            class="h-4 w-4 shrink-0 rounded-sm object-contain"
          />
          <svg v-else class="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <rect x="2.5" y="3" width="11" height="10" rx="1.2" stroke="currentColor" stroke-width="1.4" />
            <path d="M5 6h6M5 8.5h6M5 11h3.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
          </svg>
          {{ data.outlet }}
        </p>
        <p v-if="data.club" class="text-sm text-muted flex items-center gap-1.5">
          <svg class="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="5.5" cy="5" r="2" stroke="currentColor" stroke-width="1.4" />
            <path d="M1.75 13.25c.4-2.4 1.95-3.75 3.75-3.75s3.35 1.35 3.75 3.75" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
            <path d="M10.5 7a2 2 0 100-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
            <path d="M11.25 9.6c1.55.35 2.6 1.6 2.95 3.65" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
          </svg>
          {{ data.club }}
        </p>
        <p v-if="data.excerpt" class="text-sm text-ink/80 line-clamp-3 leading-relaxed">
          {{ data.excerpt }}
        </p>
      </div>
      <span class="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-semibold text-brand-green">
        {{ t("common.readMore") }}
        <svg class="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </span>
    </div>
  </a>
</template>
